import {Component, EventEmitter, Input, Optional, Output, QueryList, ViewChildren} from '@angular/core';
import {
  Competitor,
  CompetitorRegistrationDto,
  Country,
  EventItem,
  Lookups,
  Race,
  RaceRegistration, RegisterHelper,
  ManageCompetitorRequest,
  TeamType, RaceCommercialItem, RaceAdditionalPricingItem
} from "@model";
import {NgbActiveModal, NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {FormBuilder} from "@angular/forms";
import {AbstractComponent, CompetitorTableAction, CompetitorTableType, OnEditCompetitor} from "@components";
import {
  CodebookService,
  CompetitorService,
  LoaderService,
  TOAST_STATE,
  ToastService
} from "@services";
import {TranslocoService} from "@ngneat/transloco";
import {
  LookupTeamMemberLists,
  TeamMembersManagementFormComponent
} from "../team-members-management-form/team-members-management-form.component";

@Component({
  selector: 'app-team-members-management',
  templateUrl: './team-members-management.component.html',
  styleUrl: './team-members-management.component.scss'
})
export class TeamMembersManagementComponent extends AbstractComponent {

  @Input() race: Race | undefined;
  @Input({required: true}) eventItem: EventItem | undefined;
  @Input() teamMemberEdit: boolean = false;
  @Input() competitor: Competitor | undefined;
  @Input() title: string | undefined;
  @Input() currencies: Lookups[] = [];
  @Input() raceRegistration: RaceRegistration | undefined;
  @Input({required: true}) languages: Lookups[] = [];
  @Input() countries: Country[] = [];
  @Input('sizes') tshirtSizes: Lookups[] = [];
  @Input() disciplines: Lookups[] = [];
  @Input() meals: Lookups[] = [];
  @Input() transports: Lookups[] = [];
  @Input() genders: Lookups[] = [];
  @Input() registerHelper: RegisterHelper | undefined;
  @Input() assignStartNumber: boolean = false;
  @Input() raceCommercialItems: RaceCommercialItem[] = [];
  @Input() raceAdditionalItems: RaceAdditionalPricingItem[] = [];
  @Output() onSaveSuccessAction: EventEmitter<Competitor> = new EventEmitter<Competitor>();

  @ViewChildren(TeamMembersManagementFormComponent) form!: QueryList<TeamMembersManagementFormComponent>;


  filteredLists: LookupTeamMemberLists = new LookupTeamMemberLists();
  competitorRegistration: CompetitorRegistrationDto = new CompetitorRegistrationDto();

  constructor(@Optional() public activeModal: NgbActiveModal, public translocoService: TranslocoService, public formBuilder: FormBuilder,
              private codebookService: CodebookService, private modalService: NgbModal, private loaderService: LoaderService,
              private competitorService: CompetitorService, private toastService: ToastService
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToCodebookValues()
    if (this.competitor) {
      this.competitorRegistration = Competitor.competitorToDto(this.competitor);
    }
    this.filterInputLists();
    this.registerHelper = new RegisterHelper([], [], undefined, this.race?.raceSettings);
    if (!this.teamMemberEdit) {
      this.competitor?.teamMembers?.forEach((value: Competitor, index: number) => {
        this.registerHelper?.onChangeEmail(value.email ?? '', index)
      })
    }
  }

  subscribeToCodebookValues() {
    this.subscriptions.push(this.codebookService.disciplines.value.subscribe(val => {
        this.disciplines = val;
      }),
      this.codebookService.sizes.value.subscribe(val => {
        this.tshirtSizes = val;
      }),
      this.codebookService.meals.value.subscribe(val => {
        this.meals = val;
      }),
      this.codebookService.transports.value.subscribe(val => {
        this.transports = val;
      }),
      this.codebookService.countries.value.subscribe(val => {
        this.countries = val;
      }),
      this.codebookService.currencies.value.subscribe(val => {
        this.currencies = val;
      }),
      this.codebookService.genders.value.subscribe(val => {
          this.genders = val;
        })
    )

  }


  get isInModal(): boolean {
    return !!this.activeModal
  }


  filterInputLists() {
    if ((this.race?.applicationFormSettings?.meals?.length ?? 0) > 0) {
      this.filteredLists.meals = this.filterList(this.race?.applicationFormSettings?.meals, this.meals);
    }
    if ((this.race?.applicationFormSettings?.tshirts?.length ?? 0) > 0) {
      this.filteredLists.tshirtSizes = this.filterList(this.race?.applicationFormSettings?.tshirts, this.tshirtSizes);
    }
    if ((this.race?.applicationFormSettings?.disciplines?.length ?? 0) > 0) {
      this.filteredLists.disciplines = this.filterList(this.race?.applicationFormSettings?.disciplines, this.disciplines);
    }
    this.filteredLists.genders = [...this.genders];
    this.filteredLists.countries = [...this.countries];
    this.filteredLists.transports = [...this.transports];
    this.filteredLists.currencies = [...this.currencies];
  }

  filterList(ids: number[] | undefined, list: Lookups[] | undefined): Lookups[] {
    let filteredItems: Lookups[] = [];
    if ((ids?.length ?? 0) > 0 && (list?.length ?? 0) > 0) {
      for (let item of list!) {
        for (let id of ids!) {
          if (item.id === id) {
            filteredItems.push(item);
          }
        }
      }
    }
    return filteredItems;
  }

  onCancel(): void {
    this.activeModal?.dismiss();
    //todo reset
  }


  onTeamMemberSave(result: { competitor: Competitor, raceRegistration: RaceRegistration } | undefined, index: number) {
    if (!result || !this.competitor?.teamMembers?.[index]) return;
    this.competitor!.teamMembers![index] = result.competitor;
    this.competitor!.teamMembers = [...this.competitor!.teamMembers];
  }

  onCompetitorSave() {
    if (this.form.get(0)) {
      let competitorForm: TeamMembersManagementFormComponent | undefined = this.form.get(0);
      if (!!competitorForm) {
        if (competitorForm.isValid()) {
          const result: {
            competitor: Competitor,
            raceRegistration: RaceRegistration
          } | undefined = competitorForm.getData();
          if (result) {
            this.competitor = {...result.competitor}
            this.raceRegistration = {...result.raceRegistration};
            this.loaderService.startLoading()
            this.competitorService.manageCompetitor(new ManageCompetitorRequest(this.competitor, this.raceRegistration), (result: ManageCompetitorRequest) => {
              this.onSaveSuccessAction.emit(result.competitor);
              this.loaderService.dismissLoading();
            }, (errorCode?: number) => {
              this.loaderService.dismissLoading();
              this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate(
                "competitor.edit.error." + errorCode));
            });
          }
          this.activeModal?.dismiss();
        }
      }
    }
  }

  editTeamMember(value: OnEditCompetitor) {
    let {competitor, index} = value;
    const modalRef: NgbModalRef = this.modalService.open(TeamMembersManagementFormComponent, {
      size: 'lg',
      scrollable: true,
    });
    modalRef.componentInstance.competitor = competitor;
    modalRef.componentInstance.raceSettings = this.race?.raceSettings;
    modalRef.componentInstance.event = this.eventItem;
    modalRef.componentInstance.applicationFormSettings = this.race?.applicationFormSettings;
    modalRef.componentInstance.title = 'competitor.team.member.edit'
    modalRef.componentInstance.teamMemberEdit = true;
    modalRef.componentInstance.registerHelper = this.registerHelper;
    modalRef.componentInstance.openInModal = true;
    modalRef.componentInstance.lookups = this.filteredLists;
    modalRef.componentInstance.index = index;
    modalRef.componentInstance.onChangeEmail.subscribe((email: string) => {
      this.registerHelper?.onChangeEmail(email, index);
    });

    modalRef.componentInstance.onSave.subscribe(() => {
      if (modalRef.componentInstance.isValid()) {
        this.onTeamMemberSave(modalRef.componentInstance.getData(), index);
        modalRef.close();
      }
    })

    modalRef.componentInstance.onCancel.subscribe(() => {
      modalRef.close();
    })
  }

  protected readonly TeamType = TeamType;
  protected readonly CompetitorTableType = CompetitorTableType;
  protected readonly CompetitorTableAction = CompetitorTableAction;
}
